import { matchPath } from "react-router-dom";

/** Registered Pro router paths shared with deep-link validation. */
export const PRO_ROUTE_PATHS = {
  welcome: "/welcome",
  // Plan 476 M3: public Pro signup, and the one-time offer step after it.
  join: "/join",
  joinOffer: "/join/offer",
  home: "/home",
  tasks: "/tasks",
  // Retired landing route. Registered ONLY as a redirect (App.tsx) so draft
  // deep links already sent by email/SMS keep working. This is the single
  // place the retired path may be spelled (today-link-sweep.test).
  legacyToday: "/today",
  clients: "/clients",
  clientDetail: "/clients/:profileId",
  // Plan 377 M5 (REST surface): re-keyed onto contactId so the formal-offer
  // composer can reach the contact-keyed GET /people/:contactId/client-detail
  // read — the /clients/:profileId REST route + resolution shim are retired.
  clientFormalOffer: "/clients/:contactId/formal-offer",
  contacts: "/contacts",
  people: "/people",
  personDetail: "/people/:contactId",
  settings: "/settings",
  pricing: "/pricing",
  campaigns: "/campaigns",
  automations: "/automations",
  marketplaceOffers: "/marketplace/offers",
  marketplaceBrowseDetail: "/marketplace/browse/:passportID",
  // Static counter-offer entry; must be registered before the `:passportID`
  // route so "recovery" is never read as a passport id.
  marketplaceBrowseRecovery: "/marketplace/browse/recovery",
  marketplaceRequestDetail: "/marketplace/requests/:id",
  marketplaceProfile: "/marketplace/profile",
  messages: "/messages",
} as const;

/**
 * Prefer the canonical Person shell when the caller already holds the
 * professional-owned contact id. The fallback is deliberately caller-owned:
 * profile-only payloads must not trigger a client-side contact lookup.
 */
export function personRoute(
  contactId: string | null | undefined,
  fallback: string,
  returnTo?: string,
): string {
  const id = contactId?.trim();
  if (!id) return fallback;
  const person = `/people/${encodeURIComponent(id)}`;
  return returnTo
    ? `${person}?returnTo=${encodeURIComponent(returnTo)}`
    : person;
}

const RETURN_TO_ORIGIN = "https://sill.invalid";
const PERCENT_ENCODED_BYTE = /%[0-9a-f]{2}/i;

/** Only internal route return targets are accepted by the Person shell. */
export function personReturnRoute(value: string | null): string | null {
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    containsControlCharacter(value)
  ) {
    return null;
  }

  // A legacy query is decoded once by URLSearchParams before it gets here.
  // Decode encoded bytes repeatedly for validation so a nested %25 cannot
  // smuggle a backslash or protocol-relative target into router navigation.
  let decoded = value;
  for (let i = 0; i < 8 && PERCENT_ENCODED_BYTE.test(decoded); i += 1) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return null;
    }
  }
  if (
    PERCENT_ENCODED_BYTE.test(decoded) ||
    !decoded.startsWith("/") ||
    decoded.startsWith("//") ||
    decoded.includes("\\") ||
    containsControlCharacter(decoded)
  ) {
    return null;
  }

  try {
    const target = new URL(value, RETURN_TO_ORIGIN);
    const decodedTarget = new URL(decoded, RETURN_TO_ORIGIN);
    return target.origin === RETURN_TO_ORIGIN &&
      target.protocol === "https:" &&
      !target.username &&
      !target.password &&
      !isDetailReturnTarget(decodedTarget)
      ? value
      : null;
  } catch {
    return null;
  }
}

// A Person detail cannot return to a legacy/canonical detail entry point:
// those targets either start a compatibility resolver again or navigate the
// user back to the Person they are already viewing. Keep non-detail app paths
// (including their query/hash intent) valid.
function isDetailReturnTarget(target: URL): boolean {
  // matchPath is the router's own case-insensitive, optional-trailing-slash
  // matcher. `decoded` is used above so encoded route letters are classified
  // identically to the location React Router will eventually consume.
  if (matchPath({ path: "/clients/:profileId", caseSensitive: false, end: true }, target.pathname)) return true;
  if (matchPath({ path: "/people/:contactId", caseSensitive: false, end: true }, target.pathname)) return true;
  return Boolean(matchPath({ path: "/contacts", caseSensitive: false, end: true }, target.pathname)) && target.searchParams.has("contact");
}

function containsControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 0x1f || code === 0x7f;
  });
}
